import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase_client';
import { MainLayout } from '../MainLayout';

interface Organization {
  id: string;
  name: string;
  country_iso: string;
  address: string | null;
  referred_by: string | null;
  created_at: string;
}

interface Owner {
  user_id: string;
  email: string;
  phone: string;
  display_name: string | null;
}

export default function AdminEditCustomer() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [referredBySearchTerm, setReferredBySearchTerm] = useState('');
  const [showReferredByDropdown, setShowReferredByDropdown] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const emailDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Organization form data
  const [orgData, setOrgData] = useState<Organization>({
    id: '',
    name: '',
    country_iso: 'MY',
    address: '',
    referred_by: null,
    created_at: ''
  });

  // Owner form data
  const [ownerData, setOwnerData] = useState<Owner>({
    user_id: '',
    email: '',
    phone: '',
    display_name: ''
  });

  const [countries, setCountries] = useState<{iso: string, name: string, country_phone_code: string}[]>([]);
  const [organizations, setOrganizations] = useState<{id: string, name: string}[]>([]);
  const [countryPhoneCode, setCountryPhoneCode] = useState<string>('+60');

  const fetchData = useCallback(async () => {
    if (!orgId) return;

    try {
      setLoading(true);

      // Fetch organization data
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();

      if (orgError) throw orgError;
      if (org) {
        setOrgData({
          id: org.id,
          name: org.name || '',
          country_iso: org.country_iso || 'MY',
          address: org.address || '',
          referred_by: org.referred_by || null,
          created_at: org.created_at || ''
        });
      }

      // Fetch owner data
      const { data: ownerMember } = await supabase
        .from('organization_members')
        .select('user_id')
        .eq('org_id', orgId)
        .eq('role_id', 1)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (ownerMember?.user_id) {
        // Get email for this specific owner from auth.users via RPC
        const { data: ownerEmailRow, error: emailsError } = await supabase
          .rpc('get_org_owner_emails')
          .eq('user_id', ownerMember.user_id)
          .maybeSingle();

        if (emailsError) {
          console.error('Error fetching owner email:', emailsError);
        }

        const email = (ownerEmailRow as { user_id: string; email: string } | null)?.email || '';

        // Get user profile
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('contact_number, display_name')
          .eq('id', ownerMember.user_id)
          .maybeSingle();

        // Extract phone number without country code (similar to TeamMemberModal)
        const fullPhone = profile?.contact_number || '';
        let extractedCode = '+60'; // default
        let phoneWithoutCode = fullPhone;
        
        // Check if phone starts with any country code from countries table
        if (fullPhone && countries.length > 0) {
          for (const country of countries) {
            const countryCode = country.country_phone_code?.trim(); // Remove any whitespace
            if (countryCode && fullPhone.startsWith(countryCode)) {
              extractedCode = countryCode;
              phoneWithoutCode = fullPhone.substring(countryCode.length).trim();
              break;
            }
          }
        } else if (fullPhone) {
          // Fallback: use regex if countries not loaded yet or no match found
          const match = fullPhone.match(/^(\+\d{1,2})\s*(.+)/);
          if (match) {
            extractedCode = match[1];
            phoneWithoutCode = match[2].trim();
          }
        }
        
        console.log('Phone extraction:', { fullPhone, extractedCode, phoneWithoutCode, countriesLoaded: countries.length > 0 });
        
        setCountryPhoneCode(extractedCode);
        
        const resolvedEmail = typeof email === 'string' ? email : '';
        setOriginalEmail(resolvedEmail);
        setOwnerData({
          user_id: ownerMember.user_id,
          email: resolvedEmail,
          phone: phoneWithoutCode,
          display_name: profile?.display_name || ''
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrorMsg('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  }, [orgId, countries]);

  const fetchCountries = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('iso, name, country_phone_code')  
        .order('name', { ascending: true });

      if (error) throw error;
      setCountries(data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  }, []);

  const fetchOrganizations = useCallback(async () => {
    try {
      // Fetch all organizations in batches if needed
      const BATCH_SIZE = 1000;
      let allOrgs: any[] = [];
      let offset = 0;
      let hasMore = true;
  
      while (hasMore) {
        const { data, error } = await supabase
          .from('organizations')
          .select('id, name')
          .order('name', { ascending: true })
          .range(offset, offset + BATCH_SIZE - 1);
  
        if (error) throw error;
        
        if (data && data.length > 0) {
          allOrgs = [...allOrgs, ...data];
          offset += BATCH_SIZE;
          hasMore = data.length === BATCH_SIZE;
        } else {
          hasMore = false;
        }
      }
  
      setOrganizations(allOrgs);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  }, []);

  useEffect(() => {
    if (orgId) {
      fetchCountries();
      fetchOrganizations();
    }
  }, [orgId, fetchCountries, fetchOrganizations]);

  const filteredOrganizations = useMemo(() => {
    if (!referredBySearchTerm) {
      return organizations.filter(org => org.id !== orgId);
    }
    return organizations
      .filter(org => 
        org.id !== orgId && 
        org.name?.toLowerCase().includes(referredBySearchTerm.toLowerCase())
      );
  }, [organizations, orgId, referredBySearchTerm]);

  useEffect(() => {
    if (orgId && countries.length > 0) {
      fetchData();
    }
  }, [orgId, countries.length, fetchData]);

  const getCountryCode = (iso: string) => {
    const countryCodes: { [key: string]: string } = {
      'MY': '+60',
      'SG': '+65',
      'GB': '+44'
    };
    return countryCodes[iso] || '+60';
  };

  const validateEmailFormat = (email: string): string | null => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  };

  const handleEmailChange = (value: string) => {
    setOwnerData(prev => ({ ...prev, email: value }));

    // Clear any pending debounce
    if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);

    const formatError = validateEmailFormat(value);
    if (formatError) {
      setEmailError(formatError);
      setEmailChecking(false);
      return;
    }

    // Same as original — no need to check
    if (value === originalEmail) {
      setEmailError(null);
      setEmailChecking(false);
      return;
    }

    // Debounce the uniqueness check
    setEmailChecking(true);
    setEmailError(null);
    emailDebounceRef.current = setTimeout(async () => {
      const { data: inUse, error } = await supabase
        .rpc('check_email_in_use', {
          p_email: value,
          p_exclude_user_id: ownerData.user_id || null
        });

      if (error) {
        console.error('Email check error:', error);
      } else if (inUse) {
        setEmailError('This email is already in use by another account');
      }
      setEmailChecking(false);
    }, 600);
  };

  const handleSave = async () => {
    if (!orgId) return;

    // Validate email before saving
    const emailValidation = validateEmailFormat(ownerData.email);
    if (emailValidation || emailError) {
      if (emailValidation) setEmailError(emailValidation);
      return;
    }

    try {
      setSaving(true);
      setErrorMsg(null);

      if (newPassword && newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters');
      }

      // Update organization
      const { error: orgError } = await supabase
        .from('organizations')
        .update({
          name: orgData.name,
          country_iso: orgData.country_iso,
          address: orgData.address || null,
          referred_by: orgData.referred_by || null
        })
        .eq('id', orgId);

      if (orgError) throw orgError;

      // Update owner data if user_id exists
      if (ownerData.user_id) {
        // Update email if it changed
        const emailChanged = ownerData.email && ownerData.email !== originalEmail;
        if (emailChanged) {
          const { data: authData } = await supabase.auth.getUser();
          const currentUserId = authData?.user?.id;

          const { data: emailData, error: emailError } = await supabase.functions.invoke('update-user-email', {
            body: {
              user_id: ownerData.user_id,
              new_email: ownerData.email,
              current_user_id: currentUserId
            }
          });

          if (emailError) {
            throw new Error(`Failed to update email: ${emailError.message}`);
          }

          if (!emailData || !emailData.success) {
            throw new Error(emailData?.error || 'Failed to update email');
          }

          setOriginalEmail(ownerData.email);
        }

        // Update phone and display name
        const fullPhone = ownerData.phone ? `${countryPhoneCode}${ownerData.phone}` : '';

        const { error: profileError } = await supabase
          .from('user_profiles')
          .update({
            contact_number: fullPhone || null,
            display_name: ownerData.display_name || null
          })
          .eq('id', ownerData.user_id);

        if (profileError) throw profileError;

        // Optionally set a new password for the owner account
        if (newPassword) {
          const { data: authData } = await supabase.auth.getUser();
          const currentUserId = authData?.user?.id;

          const { data: pwdData, error: pwdError } = await supabase.functions.invoke('update-user-password', {
            body: {
              user_id: ownerData.user_id,
              new_password: newPassword,
              current_user_id: currentUserId
            }
          });

          if (pwdError) {
            throw new Error(`Failed to update password: ${pwdError.message}`);
          }

          if (!pwdData || !pwdData.success) {
            throw new Error(pwdData?.error || 'Failed to update password');
          }
        }
      }

      setSuccessMsg(newPassword ? 'Customer and password updated successfully' : 'Customer updated successfully');
      setNewPassword('');
      setTimeout(() => {
        navigate('/admin/customers');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving:', error);
      setErrorMsg(error.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-gray-600">Loading customer data...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="text-sm text-gray-500 mb-1">Admin Only</div>
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">Edit Customer</h1>
              <button
                onClick={() => navigate('/admin/customers')}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                ← Back to Customers
              </button>
            </div>
          </div>

          {/* Success/Error Messages */}
          {successMsg && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {successMsg}
              </div>
              <button onClick={() => setSuccessMsg(null)} className="text-green-700 hover:text-green-900">
                ×
              </button>
            </div>
          )}

          {errorMsg && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {errorMsg}
              </div>
              <button onClick={() => setErrorMsg(null)} className="text-red-700 hover:text-red-900">
                ×
              </button>
            </div>
          )}

          {/* Form */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Customer Information</h2>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Organization Data */}
                <div className="space-y-5">
                  <h3 className="text-md font-semibold text-gray-700 border-b pb-2 mb-4">
                    Organization Details
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organization Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={orgData.name}
                      onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="e.g., ABC Company Sdn Bhd"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={orgData.country_iso}
                      onChange={(e) => setOrgData({ ...orgData, country_iso: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      {countries.map(country => (
                        <option key={country.iso} value={country.iso}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={orgData.address || ''}
                      onChange={(e) => setOrgData({ ...orgData, address: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Enter organization address"
                      rows={4}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Referred By
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={referredBySearchTerm || (orgData.referred_by ? organizations.find(o => o.id === orgData.referred_by)?.name || '' : '')}
                        onChange={(e) => {
                          setReferredBySearchTerm(e.target.value);
                          setShowReferredByDropdown(true);
                          // Clear selection if user is typing
                          if (!e.target.value) {
                            setOrgData({ ...orgData, referred_by: null });
                          }
                        }}
                        onFocus={() => setShowReferredByDropdown(true)}
                        onBlur={() => {
                          // Delay to allow click on dropdown item
                          setTimeout(() => setShowReferredByDropdown(false), 200);
                        }}
                        placeholder="Search company name..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                      
                      {showReferredByDropdown && filteredOrganizations.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {filteredOrganizations.slice(0, 50).map(org => (
                            <div
                              key={org.id}
                              onClick={() => {
                                setOrgData({ ...orgData, referred_by: org.id });
                                setReferredBySearchTerm(org.name);
                                setShowReferredByDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                            >
                              {org.name}
                            </div>
                          ))}
                          {filteredOrganizations.length > 50 && (
                            <div className="px-3 py-2 text-xs text-gray-500 text-center border-t">
                              Showing first 50 of {filteredOrganizations.length} results. Type to search...
                            </div>
                          )}
                        </div>
                      )}
                      
                      {showReferredByDropdown && referredBySearchTerm && filteredOrganizations.length === 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-sm text-gray-500">
                          No companies found matching "{referredBySearchTerm}"
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Created At
                    </label>
                    <input
                      type="text"
                      value={orgData.created_at ? new Date(orgData.created_at).toLocaleDateString('en-GB') : ''}
                      disabled
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-500"
                    />
                  </div>
                </div>

                {/* Right Column - Owner Data */}
                <div className="space-y-5">
                  <h3 className="text-md font-semibold text-gray-700 border-b pb-2 mb-4">
                    Owner Details
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={ownerData.email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                        emailError ? 'border-red-400 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="owner@example.com"
                    />
                    {emailError ? (
                      <p className="text-xs text-red-500 mt-1">{emailError}</p>
                    ) : emailChecking ? (
                      <p className="text-xs text-gray-400 mt-1">Checking availability...</p>
                    ) : ownerData.email && ownerData.email !== originalEmail ? (
                      <p className="text-xs text-green-600 mt-1">Email is available and will be updated on save.</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">Changing the email will update the owner's login address.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Set New Password (Optional)
                    </label>
                    <input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Leave blank to keep current password"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Admin can set a new password directly (minimum 6 characters).
                    </p>
                  </div>

                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={ownerData.display_name || ''}
                      onChange={(e) => setOwnerData({ ...ownerData, display_name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Owner display name"
                    />
                  </div> */}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex">
                      <div className="relative">
                        <select
                          value={countryPhoneCode}
                          onChange={(e) => setCountryPhoneCode(e.target.value)}
                          className="appearance-none border border-gray-300 rounded-l-lg bg-white text-gray-700 pl-4 pr-8 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none w-26 cursor-pointer transition-all duration-150 hover:border-gray-400"
                        >
                          {countries.map(country => {
                            const code = country.country_phone_code || getCountryCode(country.iso);
                            return (
                              <option key={country.iso} value={code}>
                                {country.iso} {code}
                              </option>
                            );
                          })}
                        </select>
                        {/* Custom dropdown arrow */}
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      <input
                        type="tel"
                        value={ownerData.phone || ''}  
                        onChange={(e) => {
                          const phoneValue = e.target.value.replace(/[^0-9]/g, '');
                          setOwnerData({
                            ...ownerData,
                            phone: phoneValue
                          });
                        }}
                        className="flex-1 border border-l-0 border-gray-300 rounded-r-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        placeholder="1234567890"
                      />
                    </div>
                  </div></div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => navigate('/admin/customers')}
                  className="f-btn f-btn-soft"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || emailChecking || !orgData.name || !!emailError || !ownerData.email || (!!newPassword && newPassword.length < 6)}
                  className="f-btn f-btn-primary"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}